import { useState } from 'react';
import type { ProjectGraphNodeKind } from '@avisoma/shared';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import { Checkbox, CheckboxField } from '../../catalyst/checkbox';
import { Label } from '../../catalyst/fieldset';
import { KIND_FILTERS } from './graphModel';
import { KIND_PALETTE } from './kindPalette';

type Props = {
  hiddenKinds: ReadonlySet<ProjectGraphNodeKind>;
  onToggleKind: (kind: ProjectGraphNodeKind) => void;
  categories: string[];
  hiddenCategories: ReadonlySet<string>;
  onToggleCategory: (category: string) => void;
};

/**
 * The node type and document type filters, in a section that starts open only when something is hidden;
 * the heading counts what is hidden.
 */
export function GraphFilters({ hiddenKinds, onToggleKind, categories, hiddenCategories, onToggleCategory }: Props) {
  const hidden = hiddenKinds.size + hiddenCategories.size;
  // `null` until the user opens or closes the section.
  const [chosenOpen, setChosenOpen] = useState<boolean | null>(null);
  const open = chosenOpen ?? hidden > 0;
  const Chevron = open ? ChevronDownIcon : ChevronRightIcon;

  return (
    <div className="flex flex-col gap-4 text-sm text-zinc-900 dark:text-zinc-100">
      <button
        type="button"
        aria-expanded={open}
        className="-m-1 flex items-center gap-1.5 rounded-md p-1 text-left hover:bg-zinc-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:hover:bg-zinc-700/50"
        onClick={() => setChosenOpen(!open)}
      >
        <Chevron className="size-4 shrink-0 text-zinc-500 dark:text-zinc-400" aria-hidden />
        <span className="font-medium">Filters</span>
        {hidden > 0 && <span className="text-zinc-500 dark:text-zinc-400">· {hidden} hidden</span>}
      </button>
      {open && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <div className="font-medium">Node types</div>
            {KIND_FILTERS.map(({ kind, label }) => (
              <CheckboxField key={kind}>
                <Checkbox checked={!hiddenKinds.has(kind)} onChange={() => onToggleKind(kind)} />
                {/* The swatch is the kind's colour on the zoomed-out canvas and the minimap: the filter is the legend. */}
                <Label className="flex items-center gap-2">
                  <span aria-hidden className={clsx('size-2.5 shrink-0 rounded-sm', KIND_PALETTE[kind].swatch)} />
                  {label}
                </Label>
              </CheckboxField>
            ))}
          </div>
          {categories.length > 0 && (
            <div className="flex flex-col gap-1">
              <div className="font-medium">Document types</div>
              {categories.map((category) => (
                <CheckboxField key={category}>
                  <Checkbox checked={!hiddenCategories.has(category)} onChange={() => onToggleCategory(category)} />
                  <Label>{category}</Label>
                </CheckboxField>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
